import dayjs from 'dayjs';
import Vue from 'vue';
import { mapGetters, mapActions, mapState } from 'vuex';
import PptxGenJS from 'pptxgenjs';
import Tooltip from 'tooltip.js';
import dashQueriesIcon from 'assets/img/dashboards/dash-queries.svg';
import { BfSpinner, BfModal, BfDialog } from 'components/Butterfly';
import Util from 'src/utils/general';
import Query from 'src/api/query';
import QueryUtils, { mergeDashboardFiltersWithBotanicQuery } from "src/utils/query";
import Data from 'src/utils/data';
import { mapKeysToState } from 'components/DataWidgets/DataWidgetUtils';
import { stringify } from 'csv-stringify';
import { allWidgets, getVisibleWidgets, processFilters, addValueToQueryParam, defaultConfig } from './Dashboard.utils';
import FormatUtils from 'src/utils/formatters';
import { DateRangeTypeEnum } from 'types/DashboardTypes';
import ResizeListener from 'components/widgets/ResizeListener/ResizeListener.vue';
import Dropdown from 'components/Butterfly/Dropdown/Dropdown';
import DropdownItem from 'components/Butterfly/Dropdown/DropdownItem';
import { FETCH_DATA, FETCH_DATA_QUERY, FETCH_DATA_CONTEXT_NETWORK, SET_WIDGET_CONFIG, } from 'src/store/types';
const ModalPrompt = () => import('components/widgets/ModalPrompt.vue');
import { cloneDeep, isEqual } from 'lodash';
import { fetch_keyphrases_data, fetch_pivot_data } from 'src/store/modules/data/api';
import { dateRangeToFilter } from 'src/utils/dates';
import { isQueryValid } from 'src/components/project/analysis/results/ThemeBuilder/ThemeBuilder.utils';
const Overview = Vue.extend({
    components: {
        BfModal,
        BfDialog,
        BfSpinner,
        Dropdown,
        DropdownItem,
        ModalPrompt,
        ResizeListener,
    },
    provide() {
        return {
            featureFlags: this.featureFlags,
        };
    },
    props: {
        /** project id.  Only if in analyst view. */
        projectId: { type: Number, required: false, default: null },
        /** dashboard id.  In analyst view is integer id.  In viewer mode is a string. */
        dashboardId: { type: [Number, String], required: true },
        /** is this an analyst view?  usually passed via route */
        inAnalysis: { type: Boolean, required: true },
        /** string name of widget to display in zoom mode. */
        widget: { type: String, required: false, default: null },
        /** array of concepts */
        concepts: { type: Array, required: false, default: () => [] },
        /** route object to use to navigate back from if in zoom or query mode */
        backToRoute: { type: Object, required: false, default: null },
        /** query id.  If present, assumption is that we are in query mode */
        queryId: { type: Number, required: false, default: null },
        /** type of dashboard we are displaying */
        dashboardType: { type: String, required: false, default: 'overview' },
        /** Add a skeleton mask (used when reloading state between dashboards) */
        isLoading: { type: Boolean, required: true },
        groupBy: { type: String, required: false, default: null },
        queryRows: { type: Array, required: false, default: () => [] },
        queryRowsCompare: { type: Array, required: false, default: () => [] },
        compareMode: { type: Boolean, required: false, default: false },
        speakerField: { type: String, required: true, default: 'Member or Agent/bot' },
    },
    data() {
        return {
            dashQueriesIcon,
            dataRequirements: {},
            dataQueryRequirements: {},
            dataContextNetworkRequirements: {},
            dataKeys: {},
            currentFiltersTooltip: null,
            exportLimit: 100000,
            isExportingQuery: false,
            showDataExportLimitModal: false,
            widgetColumns: this.calculateColumns(window.innerWidth),
            viewGroupModalVisible: false,
            groupHits: null,
            showGroupValue: null,
            maxGroupHits: 500,
            numGroupHitsNotShown: 0,
            groupSourceVerbatimId: null,
        };
    },
    computed: {
        ...mapGetters([
            'currentAnalysis',
            'currentModel',
            'currentDashboard',
            'currentUser',
            'savedQueries',
            'hasNPS',
            'hasSentiment',
            'hasNumericFields',
            'featureFlags',
            'getFirstSchemaFieldNameWithType',
            'sortedFieldsLimited',
            'sortedFieldsUnlimited',
            'defaultDateField',
            'categoricalFields',
            'dateFields',
            'sortedSegmentsForFieldsLimited',
            'currentSite',
            'widgetBanners',
            'dashboardQueries',
            'dashboardWidgetConfig',
            'expandedSavedQueries',
            'expandedDashboardQueries',
            'expandedThemeGroups',
            'dashboardDateRange',
        ]),
        ...mapState({
            fetchedData(state) {
                return mapKeysToState(state.data, this.dataKeys);
            }
        }),
        emptyDataState() {
            return this.compareMode
                ? !this.numDocumentsQuery && !this.numDocumentsQueryCompare
                : !this.numDocumentsQuery;
        },
        // Check if we have a date range filter that falls outside
        // the top-level dashboard date range
        invalidDateState() {
            const isOutsideRage = (filters) => {
                let rangeIsOutside = false;
                const dashboardStart = this.dashboardDateRangeFilters.find((f) => f.op === '>=');
                const dashboardEnd = this.dashboardDateRangeFilters.find((f) => f.op === '<=');
                const dashboardField = dashboardStart?.field;
                if (!dashboardField)
                    return false;
                const filterStart = filters.filter((f) => {
                    return f.field === dashboardField && ['>=', '>'].includes(f.op);
                });
                const filterEnd = filters.filter((f) => {
                    return f.field === dashboardField && ['<=', '<'].includes(f.op);
                });
                // The date range ends before the dashboard range
                const endBefore = filterEnd.some((f) => {
                    return dayjs(f.value).isBefore(dayjs(dashboardStart.value));
                });
                // The date range starts after the dashboard range
                const startAfter = filterStart.some((f) => {
                    return dayjs(f.value).isAfter(dayjs(dashboardEnd.value));
                });
                rangeIsOutside = endBefore || startAfter;
                return rangeIsOutside;
            };
            return this.compareMode
                ? isOutsideRage(this.validQueryRows) && isOutsideRage(this.validQueryRowsCompare)
                : isOutsideRage(this.validQueryRows);
        },
        validQueryRows() {
            if (this.queryRows.length && !isQueryValid(this.queryRows))
                return [];
            return QueryUtils.convertBotanicQueriesToDashboardFilters(this.queryRows);
        },
        validQueryRowsCompare() {
            if (this.queryRowsCompare.length && !isQueryValid(this.queryRowsCompare))
                return [];
            return QueryUtils.convertBotanicQueriesToDashboardFilters(this.queryRowsCompare);
        },
        dashboardDateRangeFilters() {
            return dateRangeToFilter(this.dashboardDateRange?.dateField ?? '', this.dashboardDateRange?.type ?? DateRangeTypeEnum.ALL_TIME, this.dashboardDateRange?.dateFrom ?? '', this.dashboardDateRange?.dateTo ?? '');
        },
        dashboardMergedFilters() {
            return this.dashboardDateRangeFilters.concat(this.validQueryRows);
        },
        dashboardMergedFiltersCompare() {
            return this.dashboardDateRangeFilters.concat(this.validQueryRowsCompare);
        },
        fetchParams() {
            return {
                projectId: this.currentDashboard?.project?.id,
                dashboardId: this.currentDashboard?.id,
                analysisId: this.currentDashboard?.analysis?.id,
                topicId: this.currentDashboard?.analysis?.topic_framework_id,
                chrysalisRef: this.currentDashboard?.project?.chrysalis_ref,
                chrysalis_url: this.currentSite?.chrysalis_url,
            };
        },
        keyExtras() {
            return [this.currentDashboard?.analysis?.modified];
        },
        isView() {
            return {
                conceptOrQuery: ['query', 'concept'].includes(this.dashboardType),
                overview: this.dashboardType === 'overview',
                concept: this.dashboardType === 'concept',
                query: this.dashboardType === 'query',
                zoomed: !!this.widget,
            };
        },
        emptyWidgetState() {
            return !Object.values(this.visibleWidgets).some(Boolean);
        },
        visibleWidgets() {
            return getVisibleWidgets(this.dashboardWidgetConfig, this.isView, this.hasSentiment, this.hasNPS, this.hasNumericFields, this.hasDate, this.widget, this.compareMode, this.featureFlags);
        },
        displayGroup() {
            if (this.dashboardType === 'query')
                return [this.query.name, ...this.concepts].join(' & ');
            if (this.dashboardType === 'concept')
                return this.concepts.join(' & ');
            return 'overall__';
        },
        conceptQuery() {
            const includes = this.concepts.map((c) => {
                return {
                    "type": "match_any",
                    "includes": [
                        {
                            "type": "text", "value": c
                        }
                    ]
                };
            });
            return {
                "type": "match_all",
                "includes": includes,
                "excludes": [],
            };
        },
        // This computed prop combines the current theme query with any concepts
        // that have also been selected in the route query. It does not include
        // the dashboard filters. For that value, use filteredQuery.
        themeQuery() {
            if (this.concepts.length) {
                const conceptQs = this.concepts.map((c) => {
                    return {
                        "type": "match_any",
                        "includes": [
                            {
                                "type": "text", "value": c
                            }
                        ]
                    };
                });
                return {
                    type: 'match_all',
                    includes: [
                        this.query.query_value,
                        ...conceptQs,
                    ]
                };
            }
            return this.query.query_value;
        },
        fieldSegmentationWidgetFooterText() {
            return {
                'overview': 'Calculated relative to overall data (including filters).',
                'concept': 'Calculated relative to this concept (including filters).',
                'query': 'Calculated relative to this theme (including filters).'
            };
        },
        requiredStateIsAvailable() {
            return (!!this.currentDashboard
                && !!this.currentSite
                && !!this.currentModel
                && !!this.currentAnalysis);
        },
        analysisId() {
            return this.currentDashboard?.analysis.id;
        },
        sortedTopConceptsAsQueries() {
            // copy the concepts and sort them as expected here:
            // returning with the same Type as a regular query
            return this.currentModel?.sortedConcepts
                .slice()
                // sort by frequency (not by name)
                .sort((a, b) => a.frequencyRank - b.frequencyRank)
                // translate top concepts into "queries"
                .map((c) => ({
                name: c.name,
                query_value: {
                    type: "match_any",
                    includes: c.concept_terms?.map((term) => {
                        return {
                            type: 'text',
                            value: term,
                        };
                    }),
                },
            }));
        },
        dateFieldNames() {
            return this.dateFields?.map((f) => f.name) ?? [];
        },
        filteredAllData() {
            let query = { type: 'match_all', includes: [{ type: 'all_data' }] };
            return mergeDashboardFiltersWithBotanicQuery(query, this.dashboardMergedFilters, this.dateFieldNames);
        },
        filteredQuery() {
            let query = this.isView['query'] && this.query
                ? this.themeQuery
                : {
                    type: 'match_all',
                    includes: [
                        { type: 'all_data' },
                    ],
                };
            return mergeDashboardFiltersWithBotanicQuery(query, this.dashboardMergedFilters, this.dateFieldNames);
        },
        filteredQueryCompare() {
            let query = {
                type: 'match_all',
                includes: [
                    { type: 'all_data' },
                ],
            };
            return mergeDashboardFiltersWithBotanicQuery(query, this.dashboardMergedFiltersCompare, this.dateFieldNames);
        },
        filteredConceptQuery() {
            return mergeDashboardFiltersWithBotanicQuery(this.conceptQuery, this.dashboardMergedFilters, this.dateFieldNames);
        },
        numDocumentStats() {
            return this.fetchedData['dashboard_stats']?.data?.payload;
        },
        numVerbatimsQuery() {
            return this.fetchedData.verbatim_count?.data?.total_hits ?? 0;
        },
        numVerbatimsQueryCompare() {
            return this.fetchedData.verbatim_count_compare?.data?.total_hits ?? 0;
        },
        numVerbatimsTotal() {
            return this.fetchedData.verbatim_total?.data?.total_hits ?? 0;
        },
        numDocumentsQuery() {
            return this.numDocumentStats?.find(({ group__ }) => group__ === 'filtered_query')?.frequency_cov ?? this.numDocumentsAllData;
        },
        numDocumentsQueryCompare() {
            return this.numDocumentStats?.find(({ group__ }) => group__ === 'filtered_query_compare')?.frequency_cov ?? this.numDocumentsAllData;
        },
        numDocumentsAllData() {
            return this.numDocumentStats?.find(({ group__ }) => group__ === 'filtered_all_data')?.frequency_cov ?? this.numDocumentsTotal;
        },
        numDocumentsTotal() {
            return this.numDocumentStats?.find(({ group__ }) => group__ === 'overall__')?.frequency_cov;
        },
        updatingCount() {
            const loadingStateValues = ['fetching', undefined];
            return loadingStateValues.includes(this.fetchedData['dashboard_stats']?.status) ||
                loadingStateValues.includes(this.fetchedData['verbatim_count']?.status) ||
                loadingStateValues.includes(this.fetchedData['verbatim_total']?.status) ||
                (this.compareMode && loadingStateValues.includes(this.fetchedData['verbatim_count_compare']?.status));
        },
        segmentFields() {
            let is_defined = s => s.index !== undefined && s.index !== null;
            // O(1) structure to lookup field membership in the "limited" fields.
            const limited = new Set(this.sortedFieldsLimited.filter(is_defined).map(f => f.name));
            // Now iterate over ALL fields, returning a structure that includes
            // whether the field is also part of the limited set.
            return this.sortedFieldsUnlimited.filter(is_defined).map(f => ({
                name: f.name,
                type: f.type,
                unlimited: !limited.has(f.name),
            }));
        },
        hasDate() {
            const schema = this.currentDashboard?.project.schema;
            if (schema) {
                return Util.checkSchemaHasDate(schema);
            }
            return undefined;
        },
        hasFiles() {
            return this.currentDashboard?.project?.file_based;
        },
        documentPercentQuery() {
            return this.numDocumentsQuery ? ((this.numDocumentsQuery / this.numDocumentsTotal) * 100) : 0;
        },
        verbatimPercentQuery() {
            return this.numVerbatimsQuery ? ((this.numVerbatimsQuery / this.numVerbatimsTotal) * 100) : 0;
        },
        documentPercentQueryCompare() {
            return this.numDocumentsQueryCompare ? ((this.numDocumentsQueryCompare / this.numDocumentsTotal) * 100) : 0;
        },
        verbatimPercentQueryCompare() {
            return this.numVerbatimsQueryCompare ? ((this.numVerbatimsQueryCompare / this.numVerbatimsTotal) * 100) : 0;
        },
        documentPercentAllData() {
            return this.numDocumentsQuery && this.numDocumentsAllData > 0 ? ((this.numDocumentsQuery / this.numDocumentsAllData) * 100) : 0;
        },
        queries() {
            return this.dashboardQueries;
        },
        query() {
            return this.expandedSavedQueries.find(q => q.id === this.queryId);
        },
        rawQuery() {
            return this.savedQueries.find(q => q.id === this.queryId);
        },
        viewTitle() {
            if (this.isView['query'] && !!this.query)
                return [this.query?.name || 'THEME', ...this.concepts].join(' & ');
            if (this.isView['concept'] && this.concepts.length)
                return this.concepts.join(' & ');
            return '';
        },
        showDataExports() {
            return (!this.currentUser.viewer);
        },
        zoomToRoutes() {
            const inQuery = this.isView['query'] && !!this.queryId;
            const hasConcepts = this.concepts.length;
            const inConcept = this.isView['concept'] && hasConcepts;
            const routeName = `${this.inAnalysis ? 'analysis' : 'viewer'}${inQuery ? '-query' : ''}${inConcept ? '-concept' : ''}-datawidget-zoom`;
            const routeQuery = {
                filters: this.$route.query.filters,
                concept: [],
            };
            if (hasConcepts) {
                routeQuery['concept'] = this.concepts;
            }
            const route = {
                name: routeName,
                params: {
                    dashboardId: this.dashboardId,
                    queryId: this.queryId,
                    projectId: this.projectId,
                    analysisId: this.analysisId,
                    widget: this.widget
                },
                query: routeQuery
            };
            const widgets = [
                'verbatims',
                'themes-concepts',
                'quadrant',
                'timeline',
                'segments',
                'emergent-concepts',
                'pivot-table',
                'segment-correlation',
                'key-phrases',
                'compare-themes-concepts',
                'compare-segments',
                'compare-timeline',
            ];
            return widgets.reduce((routes, widget) => {
                routes[widget] = {
                    ...route,
                    params: {
                        ...route.params,
                        widget
                    }
                };
                return routes;
            }, {});
        },
        filterQuery() {
            return {
                name: 'filter',
                value: QueryUtils.convertDashboardFiltersToBotanicQueries(this.dashboardMergedFilters, this.dateFieldNames)
            };
        },
        filterQueryCompare() {
            return {
                name: 'filter_compare',
                value: QueryUtils.convertDashboardFiltersToBotanicQueries(this.dashboardMergedFiltersCompare, this.dateFieldNames)
            };
        },
        widgetProps() {
            const shared = {
                'nps-timeline': {
                    props: {
                        'is-zoomed': this.widget === 'nps-timeline',
                        'zoom-to-route': this.zoomToRoutes['nps-timeline'],
                        'banner': this.widgetBanners['nps-timeline'],
                        'group': this.displayGroup,
                        'overall-data': this.fetchedData['nps-timeline-overall'],
                        'date-fields': this.currentModel.dateFields,
                        'default-date-field': this.defaultDateField,
                        'week-start': this.currentDashboard?.project?.week_start,
                        'dev-mode': this.featureFlags.dev_mode,
                        'export-name': this.currentDashboard.name,
                        'widget-config': true,
                        'config': this.getWidgetConfig('nps-timeline'),
                        'day-first-dates': this.currentDashboard.project.day_first_dates,
                        ...this.fetchedData['nps-timeline'],
                    },
                    events: {
                        'requires': this.fetchWidgetData,
                        'config-changed': (e) => this.configChanged('nps-timeline', e),
                    },
                },
                'sentiment-timeline': {
                    props: {
                        ...this.fetchedData['sentiment-timeline'],
                        'is-zoomed': this.widget === 'sentiment-timeline',
                        'zoom-to-route': this.zoomToRoutes['sentiment-timeline'],
                        'banner': this.widgetBanners['sentiment-timeline'],
                        'group': this.displayGroup,
                        'overall-data': this.fetchedData['sentiment-timeline-overall'],
                        'date-fields': this.currentModel.dateFields,
                        'default-date-field': this.defaultDateField,
                        'week-start': this.currentDashboard?.project?.week_start,
                        'dev-mode': this.featureFlags.dev_mode,
                        'export-name': this.currentDashboard.name,
                        'config': this.getWidgetConfig('sentiment-timeline'),
                        'day-first-dates': this.currentDashboard.project.day_first_dates,
                    },
                    events: {
                        'requires': this.fetchWidgetData,
                        'config-changed': (e) => this.configChanged('sentiment-timeline', e),
                    },
                },
                'timeline': {
                    props: {
                        ...(this.isView['conceptOrQuery'] ? this.fetchedData['timeline_query'] : this.fetchedData['timeline']),
                        'banner': this.widgetBanners['timeline'],
                        'is-zoomed': this.widget === 'timeline',
                        'field-frequency': this.fetchedData['field_frequency'],
                        'dev-mode': this.featureFlags.dev_mode,
                        'export-name': this.currentDashboard.name,
                        'has-nps': this.hasNPS,
                        'has-sentiment': this.hasSentiment,
                        'has-numeric-fields': this.hasNumericFields,
                        'queries': this.expandedDashboardQueries,
                        'theme-groups': this.expandedThemeGroups,
                        'segment-fields': this.segmentFields,
                        'viewing-query': this.isView['conceptOrQuery'],
                        'concept-query': this.isView['concept'] ? this.conceptQuery : null,
                        'date-fields': this.currentModel.dateFields,
                        'default-date-field': this.defaultDateField,
                        'week-start': this.currentDashboard?.project?.week_start,
                        'zoom-to-route': this.zoomToRoutes['timeline'],
                        'sorted-segments-for-fields-limited': this.sortedSegmentsForFieldsLimited,
                        'group': this.displayGroup,
                        'schema': this.currentDashboard.project.schema,
                        'config': this.getWidgetConfig('timeline'),
                        'day-first-dates': this.currentDashboard.project.day_first_dates,
                    },
                    events: {
                        'requires': this.fetchWidgetData,
                        'config-changed': (e) => this.configChanged('timeline', e),
                    },
                },
                'segments': {
                    props: {
                        'banner': this.widgetBanners['segments'],
                        'group': this.displayGroup,
                        'filter-query': this.isView['overview'] && this.dashboardMergedFilters.length > 0 ? this.filterQuery : null,
                        'is-zoomed': this.widget === 'segments',
                        'export-name': this.currentDashboard.name,
                        'data': this.fetchedData['segmentation'],
                        'segment-fields': this.segmentFields,
                        'dev-mode': this.featureFlags.dev_mode,
                        'has-nps': this.hasNPS,
                        'mode': this.dashboardType,
                        'zoom-to-route': this.zoomToRoutes['segments'],
                        'max-rows': this.widget === 'segments' ? null : 11,
                        'footer-text': this.fieldSegmentationWidgetFooterText[this.dashboardType],
                        'config': this.getWidgetConfig('segments'),
                    },
                    events: {
                        'requires': this.fetchWidgetData,
                        'segment-clicked': this.toggleFilter,
                        'config-changed': (e) => this.configChanged('segments', e),
                    },
                },
                'emergent-concepts': {
                    props: {
                        'dev-mode': this.featureFlags.dev_mode,
                        'banner': this.widgetBanners['emergent-concepts'],
                        'base-query': {
                            query: [this.filteredQuery],
                            concept: [this.filteredConceptQuery]
                        }[this.isView['concept'] ? 'concept' : 'query'],
                        'topic-id': this.currentDashboard.analysis.topic_framework_id,
                        'project-id': this.currentDashboard.project.id,
                        'chrysalis-ref': this.currentDashboard.project.chrysalis_ref,
                        'chrysalis-url': this.currentSite.chrysalis_url,
                        'saved-queries': this.savedQueries,
                        'is-zoomed': this.widget === 'emergent-concepts',
                        'export-name': this.currentDashboard.name,
                        'zoom-to-route': this.zoomToRoutes['emergent-concepts'],
                        'date-fields': this.currentModel.dateFields,
                        'default-date-field': this.defaultDateField,
                        'show-drilldown': this.dashboardType === 'overview',
                        'config': this.getWidgetConfig('emergent-concepts'),
                        'groupby-not-supported': !!this.groupBy,
                    },
                    events: {
                        'concept-clicked': (concept) => this.navigateToConcept(concept),
                        'config-changed': (e) => this.configChanged('emergent-concepts', e),
                    },
                },
            };
            if (this.dashboardType === 'overview') {
                return {
                    ...shared,
                    'compare-nps-timeline': {
                        props: {
                            'slice-one-filters': this.dashboardMergedFilters,
                            'slice-two-filters': this.dashboardMergedFiltersCompare,
                            'dashboard-date-filters': this.dashboardDateRangeFilters,
                            'slice-one-name': this.currentDashboard.slice1_name,
                            'slice-two-name': this.currentDashboard.slice2_name,
                            'slice-one-data': this.fetchedData['compare-nps-timeline-slice-one'],
                            'slice-two-data': this.fetchedData['compare-nps-timeline-slice-two'],
                            'slice-one-data-total': this.fetchedData['compare-nps-timeline-overall-slice-one'],
                            'slice-two-data-total': this.fetchedData['compare-nps-timeline-overall-slice-two'],
                            'is-zoomed': this.widget === 'compare-nps-timeline',
                            'zoom-to-route': this.zoomToRoutes['compare-nps-timeline'],
                            'banner': this.widgetBanners['compare-nps-timeline'],
                            'group': this.displayGroup,
                            'date-fields': this.currentModel.dateFields,
                            'default-date-field': this.defaultDateField,
                            'week-start': this.currentDashboard?.project?.week_start,
                            'dev-mode': this.featureFlags.dev_mode,
                            'export-name': this.currentDashboard.name,
                            'widget-config': true,
                            'config': this.getWidgetConfig('compare-nps-timeline'),
                            'day-first-dates': this.currentDashboard.project.day_first_dates,
                        },
                        events: {
                            'requires': this.fetchWidgetData,
                            'config-changed': (e) => this.configChanged('compare-nps-timeline', e),
                        },
                    },
                    'compare-sentiment-timeline': {
                        props: {
                            'slice-one-data': this.fetchedData['compare-sentiment-timeline-slice-one'],
                            'slice-two-data': this.fetchedData['compare-sentiment-timeline-slice-two'],
                            'slice-one-name': this.currentDashboard.slice1_name,
                            'slice-two-name': this.currentDashboard.slice2_name,
                            'is-zoomed': this.widget === 'compare-sentiment-timeline',
                            'zoom-to-route': this.zoomToRoutes['compare-sentiment-timeline'],
                            'banner': this.widgetBanners['compare-sentiment-timeline'],
                            'group': this.displayGroup,
                            'slice-one-data-total': this.fetchedData['compare-sentiment-timeline-slice-one-total'],
                            'slice-two-data-total': this.fetchedData['compare-sentiment-timeline-slice-two-total'],
                            'slice-one-filters': this.dashboardMergedFilters,
                            'slice-two-filters': this.dashboardMergedFiltersCompare,
                            'date-fields': this.currentModel.dateFields,
                            'default-date-field': this.defaultDateField,
                            'week-start': this.currentDashboard?.project?.week_start,
                            'dev-mode': this.featureFlags.dev_mode,
                            'export-name': this.currentDashboard.name,
                            'config': this.getWidgetConfig('compare-sentiment-timeline'),
                            'day-first-dates': this.currentDashboard.project.day_first_dates,
                        },
                        events: {
                            'requires': this.fetchWidgetData,
                            'config-changed': (e) => this.configChanged('compare-sentiment-timeline', e),
                        },
                    },
                    'compare-timeline': {
                        props: {
                            'slice-one-data': this.fetchedData['compare-timeline-slice-one'],
                            'slice-two-data': this.fetchedData['compare-timeline-slice-two'],
                            'slice-one-name': this.currentDashboard.slice1_name,
                            'slice-two-name': this.currentDashboard.slice2_name,
                            'slice-one-filters': this.dashboardMergedFilters,
                            'slice-two-filters': this.dashboardMergedFiltersCompare,
                            'field-frequency-slice-one': this.fetchedData['field-frequency-slice-one'],
                            'field-frequency-slice-two': this.fetchedData['field-frequency-slice-two'],
                            'banner': this.widgetBanners['compare-timeline'],
                            'is-zoomed': this.widget === 'compare-timeline',
                            'dev-mode': this.featureFlags.dev_mode,
                            'export-name': this.currentDashboard.name,
                            'has-nps': this.hasNPS,
                            'has-sentiment': this.hasSentiment,
                            'has-numeric-fields': this.hasNumericFields,
                            'queries': this.expandedDashboardQueries,
                            'theme-groups': this.expandedThemeGroups,
                            'segment-fields': this.segmentFields,
                            'date-fields': this.currentModel.dateFields,
                            'default-date-field': this.defaultDateField,
                            'week-start': this.currentDashboard?.project?.week_start,
                            'zoom-to-route': this.zoomToRoutes['compare-timeline'],
                            'sorted-segments-for-fields-limited': this.sortedSegmentsForFieldsLimited,
                            'group': this.displayGroup,
                            'schema': this.currentDashboard.project.schema,
                            'config': this.getWidgetConfig('compare-timeline'),
                        },
                        events: {
                            'requires': this.fetchWidgetData,
                            'config-changed': (e) => this.configChanged('compare-timeline', e),
                        },
                    },
                    'compare-segments': {
                        props: {
                            'banner': this.widgetBanners['compare-segments'],
                            'group': this.displayGroup,
                            'slice-one-name': this.currentDashboard.slice1_name,
                            'slice-two-name': this.currentDashboard.slice2_name,
                            'slice-one-query': this.isView['overview'] && this.dashboardMergedFilters.length > 0 ? this.filterQuery : null,
                            'slice-two-query': this.isView['overview'] && this.dashboardMergedFiltersCompare.length > 0 ? this.filterQueryCompare : null,
                            'is-zoomed': this.widget === 'compare-segments',
                            'export-name': this.currentDashboard.name,
                            'data-slice1': this.fetchedData['segmentation_slice1'],
                            'data-slice2': this.fetchedData['segmentation_slice2'],
                            'segment-fields': this.segmentFields,
                            'dev-mode': this.featureFlags.dev_mode,
                            'has-nps': this.hasNPS,
                            'mode': this.dashboardType,
                            'zoom-to-route': this.zoomToRoutes['compare-segments'],
                            'max-rows': this.widget === 'compare-segments' ? null : 11,
                            'footer-text': this.fieldSegmentationWidgetFooterText[this.dashboardType],
                            'config': this.getWidgetConfig('compare-segments'),
                        },
                        events: {
                            'requires': this.fetchWidgetData,
                            'segment-clicked': this.toggleFilter,
                            'config-changed': (e) => this.configChanged('compare-segments', e),
                        },
                    },
                    'pivot-table': {
                        props: {
                            'baseQuery': [],
                            'schema': this.currentDashboard.project.schema,
                            'config': this.getWidgetConfig('pivot-table'),
                            'is-zoomed': this.widget === 'pivot-table',
                            'zoom-to-route': this.zoomToRoutes['pivot-table'],
                            'saved-queries': this.expandedDashboardQueries,
                            'theme-groups': this.expandedThemeGroups,
                            'current-model': this.currentModel,
                            'concepts': this.sortedTopConceptsAsQueries,
                            'export-name': this.currentDashboard.name,
                            'dashboard-id': this.currentDashboard.id,
                            'has-sentiment': this.hasSentiment,
                            'has-nps': this.hasNPS,
                            'week-start': this.currentDashboard?.project?.week_start,
                            ...this.fetchedData['pivot-table'],
                        },
                        events: {
                            'requires': this.fetchWidgetData,
                            'config-changed': (e) => this.configChanged('pivot-table', e),
                        }
                    },
                    'themes-concepts': {
                        props: {
                            'data': this.fetchedData['themes-concepts'],
                            'banner': this.widgetBanners['themes-concepts'],
                            'dev-mode': this.featureFlags.dev_mode,
                            'export-name': this.currentDashboard.name,
                            'has-nps': this.hasNPS,
                            'has-numeric-fields': this.hasNumericFields,
                            'has-sentiment': this.hasSentiment,
                            'footer-text': "Calculated relative to overall data (including filters).",
                            'is-zoomed': this.widget === 'themes-concepts',
                            'queries': this.expandedDashboardQueries,
                            'theme-groups': this.expandedThemeGroups,
                            'concepts': this.sortedTopConceptsAsQueries,
                            'is-filtered': this.dashboardMergedFilters.length > 0,
                            'segment-fields': this.segmentFields,
                            'zoom-to-route': this.zoomToRoutes['themes-concepts'],
                            'config': this.getWidgetConfig('themes-concepts'),
                        },
                        events: {
                            'requires': this.fetchWidgetData,
                            'requires-phrases': this.fetchPhrases,
                            'scroll-to-top': () => this.$emit('scroll-to-top'),
                            'config-changed': (e) => this.configChanged('themes-concepts', e),
                            'go-to-concept': this.navigateToConcept,
                            'go-to-theme': this.navigateToTheme,
                        },
                    },
                    'compare-themes-concepts': {
                        props: {
                            'slice-one-filters': this.dashboardMergedFilters,
                            'slice-two-filters': this.dashboardMergedFiltersCompare,
                            'slice-one-name': this.currentDashboard.slice1_name,
                            'slice-two-name': this.currentDashboard.slice2_name,
                            'data': this.fetchedData['compare-themes-concepts'],
                            'banner': this.widgetBanners['compare-themes-concepts'],
                            'dev-mode': this.featureFlags.dev_mode,
                            'export-name': this.currentDashboard.name,
                            'has-nps': this.hasNPS,
                            'has-numeric-fields': this.hasNumericFields,
                            'has-sentiment': this.hasSentiment,
                            'footer-text': "Calculated relative to overall data (including filters).",
                            'is-zoomed': this.widget === 'compare-themes-concepts',
                            'queries': this.expandedDashboardQueries,
                            'theme-groups': this.expandedThemeGroups,
                            'concepts': this.sortedTopConceptsAsQueries,
                            'is-filtered': this.dashboardMergedFilters.length > 0,
                            'segment-fields': this.segmentFields,
                            'zoom-to-route': this.zoomToRoutes['compare-themes-concepts'],
                            'config': this.getWidgetConfig('compare-themes-concepts'),
                        },
                        events: {
                            'requires': this.fetchWidgetData,
                            'requires-phrases': this.fetchPhrases,
                            'scroll-to-top': () => this.$emit('scroll-to-top'),
                            'config-changed': (e) => this.configChanged('compare-themes-concepts', e),
                            'go-to-concept': this.navigateToConcept,
                            'go-to-theme': this.navigateToTheme,
                        },
                    },
                    'segment-correlation': {
                        props: {
                            'dev-mode': this.featureFlags.dev_mode,
                            'is-zoomed': this.widget === 'segment-correlation',
                            'zoom-to-route': this.zoomToRoutes['segment-correlation'],
                            'queries': this.expandedDashboardQueries,
                            'schema': this.currentDashboard.project.schema,
                            'config': this.getWidgetConfig('segment-correlation'),
                            'go-to-pivot': this.goToPivot,
                            'documentCount': this.numDocumentsTotal,
                            'documentQuery': this.numDocumentsQuery,
                            'totalDocsCount': this.currentDashboard.project.data_units,
                            'dashboard-id': this.currentDashboard.id,
                            'export-name': this.currentDashboard.name,
                            'groupby-not-supported': !!this.groupBy,
                            'to-query-route': this.inAnalysis
                                ? { name: 'analysis-dashboard-query-view', params: { analysisId: this.analysisId, projectId: this.projectId, queryId: null }, query: { filters: this.$route.query.filters } }
                                : { name: 'dashboard-query-view', params: { dashboardId: this.dashboardId, queryId: null }, query: { filters: this.$route.query.filters } },
                            ...this.fetchedData['segment-correlation'],
                        },
                        events: {
                            'requires': this.fetchWidgetData,
                            'config-changed': (e) => this.configChanged('segment-correlation', e),
                            'toggle-filter': this.toggleFilter,
                            'scroll-to-top': () => this.$emit('scroll-to-top'),
                        },
                    },
                    'quadrant': {
                        props: {
                            'banner': this.widgetBanners['quadrant'],
                            'is-zoomed': this.widget === 'quadrant',
                            'data': this.fetchedData['quadrant'],
                            'dev-mode': this.featureFlags.dev_mode,
                            'export-name': this.currentDashboard.name,
                            'has-nps': this.hasNPS,
                            'has-numeric-fields': this.hasNumericFields,
                            'has-sentiment': this.hasSentiment,
                            'segment-fields': this.segmentFields,
                            'sorted-segments-per-field': this.sortedSegmentsForFieldsLimited,
                            'queries': this.expandedDashboardQueries,
                            'theme-groups': this.expandedThemeGroups,
                            'zoom-to-route': this.zoomToRoutes['quadrant'],
                            'concepts': this.sortedTopConceptsAsQueries,
                            'config': this.getWidgetConfig('quadrant'),
                        },
                        events: {
                            'requires': this.fetchWidgetData,
                            'config-changed': (e) => this.configChanged('quadrant', e),
                            'segment-clicked': this.toggleFilter,
                            'scroll-to-top': () => this.$emit('scroll-to-top'),
                            'go-to-concept': this.navigateToConcept,
                            'go-to-theme': this.navigateToTheme,
                        },
                    },
                };
            }
            else {
                return {
                    ...shared,
                    'summary': {
                        props: {
                            'masked': this.isLoading,
                            'dev-mode': this.featureFlags.dev_mode,
                            'dashboard-id': this.currentDashboard.id,
                            'project-id': this.currentDashboard.project.id,
                            'current-site': this.currentSite,
                            'current-project': this.currentDashboard.project,
                            'current-analysis': this.currentDashboard.analysis,
                            'query': this.isView['query'] ? this.themeQuery : this.conceptQuery,
                            'merged-filters': this.dashboardMergedFilters,
                            'saved-queries': this.expandedDashboardQueries,
                            'theme-name': this.viewTitle,
                        },
                        events: {
                            'add-concept': this.addConceptToView,
                            'segment-clicked': this.toggleFilter,
                            'config-changed': (e) => this.configChanged('summary', e),
                        },
                    },
                    'context-network': {
                        props: {
                            'banner': this.widgetBanners['context-network'],
                            'dev-mode': this.featureFlags.dev_mode,
                            'export-name': this.currentDashboard.name,
                            'query': this.isView['query'] ? this.themeQuery : this.conceptQuery,
                            ...this.fetchedData['context-network'],
                            'view-title': this.viewTitle,
                        },
                        events: {
                            'requires': this.fetchContextNetworkData,
                            'add-concept': this.addConceptToView,
                            'go-to-concept': this.navigateToConcept,
                        },
                    },
                    'verbatims': {
                        props: {
                            'banner': this.widgetBanners['verbatims'],
                            'query': this.isView['query'] ? this.themeQuery : this.conceptQuery,
                            'group-by-field': this.groupBy,
                            'saved-queries': this.savedQueries,
                            'has-nps': this.hasNPS,
                            'has-sentiment': this.hasSentiment,
                            'has-date': this.hasDate,
                            'has-files': this.hasFiles,
                            'dev-mode': this.featureFlags.dev_mode,
                            'nps-field-name': this.getFirstSchemaFieldNameWithType('NPS'),
                            'model-topics': this.currentModel.topics,
                            'model-terms': this.currentModel.terms,
                            'model-colors': this.currentModel.conceptColours,
                            'is-zoomed': this.widget === 'verbatims',
                            'zoom-to-route': this.zoomToRoutes['verbatims'],
                            'show-annotations': this.currentDashboard && this.currentDashboard.project.show_verbatim_annotations,
                            'sentiment-classifier': this.currentDashboard && this.currentDashboard.project.sentiment_classifier,
                            ...this.fetchedData['verbatims'],
                            'config': this.getWidgetConfig('verbatims'),
                        },
                        events: {
                            'requires': this.fetchUnstructuredData,
                            'config-changed': (e) => this.configChanged('verbatims', e),
                            'show-grouped-records': this.showGroupedRecords,
                        },
                    },
                    'query-details': {
                        props: {
                            'banner': this.widgetBanners['query-details'],
                            'dev-mode': this.featureFlags.dev_mode,
                            'query': this.rawQuery,
                            'saved-queries': this.savedQueries,
                            'project-id': this.projectId || (this.currentDashboard && this.currentDashboard.project && this.currentDashboard.project.id),
                            'analysis-id': this.analysisId,
                            'date-field-index': this.currentModel.dateFieldIndex || {},
                            'concept-colours': this.currentModel.conceptColours,
                            'user-is-viewer': !!this.currentUser.viewer,
                        },
                        events: {},
                    },
                    'key-phrases': {
                        props: {
                            'dev-mode': this.featureFlags.dev_mode,
                            'queries': this.expandedDashboardQueries,
                            'group': this.displayGroup,
                            'export-name': this.currentDashboard.name,
                            'zoom-to-route': this.zoomToRoutes['key-phrases'],
                            'is-zoomed': this.widget === 'key-phrases',
                            'show-drilldown': true,
                            'config': this.getWidgetConfig('key-phrases'),
                            'groupby-not-supported': !!this.groupBy,
                            'banner': this.widgetBanners['key-phrases'],
                            ...this.fetchedData['key-phrases'],
                            'view-title': this.viewTitle,
                        },
                        events: {
                            'requires': this.fetchWidgetData,
                            'phrase-clicked': (phrase) => this.navigateToConcept(phrase),
                            'add-concept': this.addConceptToView,
                            'config-changed': (e) => this.configChanged('key-phrases', e),
                        },
                    },
                };
            }
        },
        widgetList() {
            const filteredWidgets = allWidgets.filter(({ name }) => this.visibleWidgets[name]);
            const numColumns = Math.min(3, // max columns
            filteredWidgets.length, this.widgetColumns);
            // How much weight each column should have to be roughly equal in height
            const weightSum = filteredWidgets.reduce((sum, { weight }) => sum + weight, 0);
            // We must allow at least the weight of the largest widget for it to fit
            const weightMax = Math.max(...filteredWidgets.map(({ weight }) => weight));
            const weightPerColumn = Math.max(weightMax, Math.ceil(weightSum / numColumns));
            const columns = [];
            for (let i = 0; i < numColumns; i++) {
                const column = [];
                let columnWeight = 0;
                while (filteredWidgets.length) {
                    // Continue to the next column if
                    // the current one is too full
                    const widget = filteredWidgets[0];
                    columnWeight += widget.weight;
                    if (columnWeight > weightPerColumn && (i + 1) < numColumns)
                        break;
                    column.push(widget);
                    filteredWidgets.shift();
                }
                columns.push(column);
            }
            const renderColumns = [];
            for (const column of columns) {
                if (column.length)
                    renderColumns.push(column);
            }
            return renderColumns;
        },
    },
    metaInfo() {
        let title = '';
        if (this.isView['query'] && !!this.query) {
            title = [`${this.query.name} Theme`, ...this.concepts].join(' & ');
        }
        if (this.isView['concept'] && this.concepts.length) {
            title = `${this.concepts.join(' & ')} Concept`;
        }
        if (this.isView['overview']) {
            title = `${this.currentDashboard.name} Dashboard - Kapiche`;
        }
        return {
            title: `${title} ${this.isView['zoomed'] ? this.widget : 'View'} - Kapiche`
        };
    },
    watch: {
        widget() {
            this.fetchDataWidgetsData();
        },
        groupBy() {
            this.updateDocumentStats();
            this.fetchDataWidgetsData();
        },
        currentDashboard(val) {
            if (val) {
                this.fetchData();
            }
        },
        dashboardType: {
            immediate: true,
            handler(value) {
                this.$emit('set-dashboard-type', value, {
                    query: this.query?.name,
                    concepts: this.concepts,
                });
            }
        },
        compareMode(val, oldVal) {
            // Fetch data when compare mode is turned on
            if (!oldVal && val) {
                this.fetchData();
                this.fetchDataWidgetsData();
            }
        },
        dashboardMergedFiltersCompare() {
            if (!this.currentDashboard)
                return;
            this.fetchData();
            this.fetchDataWidgetsData();
        },
        dashboardMergedFilters(val) {
            if (!this.currentDashboard)
                return;
            this.fetchData();
            this.fetchDataWidgetsData();
            // Analytics
            const segmentFields = new Set();
            const segments = new Set();
            let dateField;
            if (Array.isArray(val)) {
                val.forEach((v) => {
                    if (this.dateFields.find((f) => f.name === v.field)) {
                        dateField = v.field;
                    }
                    else {
                        segmentFields.add(v.field);
                        segments.add(v.segment);
                    }
                });
            }
            this.$analytics.track.dashboard.applySegmentFilter(this.currentDashboard.id, this.currentUser.viewer ? true : false, Array.from(segmentFields), segments.size, dateField !== undefined);
        },
        queries() {
            this.fetchDataWidgetsData();
        },
        concepts(newConcepts, oldConcepts) {
            if (!isEqual(oldConcepts, newConcepts)) {
                this.fetchData();
                this.trackViewEvent();
            }
        },
        queryId(newId, oldId) {
            if (oldId !== newId) {
                this.fetchData();
                this.fetchDataWidgetsData();
            }
            this.trackViewEvent();
        },
        $route: {
            immediate: true,
            handler(to) {
                if (to.name.toLowerCase().endsWith('zoom')) {
                    this.$analytics.track.dashboard.zoomWidget(this.dashboardId, this.currentUser.viewer, to.params.widget);
                }
            }
        }
    },
    mounted() {
        this.fetchData();
        this.trackViewEvent();
    },
    methods: {
        ...mapActions({
            FETCH_DATA,
            FETCH_DATA_QUERY,
            FETCH_DATA_CONTEXT_NETWORK,
        }),
        number: FormatUtils.number,
        // Analytics for viewing dashboard
        trackViewEvent() {
            if (!this.currentDashboard)
                return;
            switch (this.dashboardType) {
                case 'query': {
                    // possible that the query requested doesn't exist so abort here
                    if (!this.query)
                        return;
                    this.$analytics.track.dashboard.viewTheme(this.currentDashboard.id, !!this.currentUser.viewer, this.query.id, [this.query.name, ...this.concepts].join(' & '));
                    break;
                }
                case 'concept': {
                    this.$analytics.track.dashboard.viewConcept(this.currentDashboard.id, !!this.currentUser.viewer, this.concepts);
                    break;
                }
                case 'overview': {
                    this.$analytics.track.dashboard.view(this.currentDashboard.id, this.currentDashboard?.queries?.length, !!this.currentUser.viewer, 2, {
                        hasNPS: this.hasNPS,
                        hasSentiment: this.hasSentiment,
                        hasDate: this.hasDate
                    });
                    break;
                }
            }
        },
        getConceptColor(name) {
            return this.currentModel.conceptColours[name] ?? '#cbcbcb';
        },
        _getIntensity(hit, type) {
            const intensityScores = {
                negative: Math.max(0, hit._attributes[`${Data.INVISIBLE_PREFIX}plumeria_negative`]),
                positive: Math.max(0, hit._attributes[`${Data.INVISIBLE_PREFIX}plumeria_positive`]),
                neutral: Math.max(0, hit._attributes[`${Data.INVISIBLE_PREFIX}plumeria_neutral`])
            };
            const totalIntensity = Object.values(intensityScores).reduce((a, b) => a + b);
            if (isNaN(totalIntensity)) {
                // If we can't access the correct plumeria outputs,
                // just set intensity to 1
                return 1;
            }
            return intensityScores[type] / totalIntensity;
        },
        getSentimentColour(hit) {
            const sentiment = hit['_attributes']['sentiment'];
            if (sentiment === 'negative') {
                return `rgb(238, 56, 36, ${this._getIntensity(hit, sentiment)})`;
            }
            else if (sentiment === 'positive') {
                return `rgb(33, 186, 69, ${this._getIntensity(hit, sentiment)})`;
            }
            else if (sentiment === 'mixed') {
                return 'rgb(248, 149, 22, 0.5)';
                /*
                // Gradient for mixed is cool, but potentially misleading as
                // it gives false implication about where in the verbatim the
                // positive or negative sentiment occurs. Needs more thought.
                const pos = getIntensity('positive')
                const neg = getIntensity('negative')
                const mid = pos / (pos + neg) * 100
                return `linear-gradient(rgb(33, 186, 69, ${pos}) ${mid - 1}%, ${mid}%, rgb(238, 56, 36, ${neg})) ${mid + 1}%`
                */
            }
            return `rgb(127, 127, 127, ${this._getIntensity(hit, sentiment)})`;
        },
        getSentimentTooltip(hit) {
            const sent = hit['_attributes']['sentiment'];
            if (sent === 'mixed') {
                // Mixed sentiment is based on logits at the sentence level
                // We only have verbatim-level logits, so we can't display intensity
                return 'Sentiment: Mixed';
            }
            return `Sentiment: ${sent.charAt(0).toUpperCase() + sent.slice(1)} \nIntensity: ${this._getIntensity(hit, sent).toFixed(2)}`;
        },
        async fetchDataWidgetsData() {
            // TODO: if null then request all, otherwise just fetch that widget
            // need a canonical list of the widgets that can be used in the url
            // or otherwise a mapping of them to do this
            Object.keys(this.dataRequirements).forEach(widgetName => {
                this.fetchWidgetData(widgetName, ...this.dataRequirements[widgetName]);
            });
            if (this.isView['conceptOrQuery']) {
                Object.keys(this.dataQueryRequirements).forEach(widgetName => {
                    this.fetchUnstructuredData(widgetName, this.dataQueryRequirements[widgetName]);
                });
                Object.keys(this.dataContextNetworkRequirements).forEach(widgetName => {
                    this.fetchContextNetworkData(widgetName, ...this.dataContextNetworkRequirements[widgetName]);
                });
            }
        },
        async fetchPhrases(id, widget_requirements, force = false, use_filters = true, fetch_method = fetch_keyphrases_data) {
            if (!this.currentDashboard)
                return;
            // If `use_filters` is set to false then dashboard filters are not included in the call to FETCH_DATA
            // This option is needed for the segmentation chart widget to support observed/expected for filters
            // on the overview dashboard.
            const filters = use_filters ? processFilters(this.dashboardMergedFilters) : [];
            const requirements = { ...widget_requirements };
            const key = await this.FETCH_DATA({
                fetch_method,
                requirements,
                filters,
                force,
                params: this.fetchParams,
                keyExtras: this.keyExtras
            });
            Vue.set(this.dataRequirements, id, [widget_requirements, force, use_filters, fetch_method]);
            Vue.set(this.dataKeys, id, key);
        },
        async fetchWidgetData(id, widget_requirements, force = false, use_filters = true, fetch_method = fetch_pivot_data) {
            if (!this.currentDashboard)
                return;
            // If `use_filters` is set to false then dashboard filters are not included in the call to FETCH_DATA
            // This option is needed for the segmentation chart widget to support observed/expected for filters
            // on the overview dashboard.
            const filters = use_filters
                ? processFilters(use_filters === true ? this.dashboardMergedFilters : use_filters)
                : [];
            const requirements = { ...widget_requirements };
            // if viewing a query dashboard then only include that query in
            // the requirements payload
            if (this.isView['query']) {
                requirements.queries = [{
                        name: [this.query.name, ...this.concepts].join(' & '),
                        value: this.themeQuery
                    }];
            }
            // viewing a concept dashboard then only use a query for that
            // concept in the requirements payload
            if (this.dashboardType === 'concept') {
                requirements.queries = [{ name: this.concepts.join(' & '), value: this.conceptQuery }];
            }
            if (!this.isView['conceptOrQuery'] && requirements.queries) {
                requirements.queries = requirements.queries.map(q => {
                    const sq = this.expandedSavedQueries.find(e => e.name === q.name);
                    return {
                        name: sq?.name || q.name,
                        value: sq?.query_value || q.value
                    };
                });
            }
            if (this.groupBy) {
                requirements.groupby = this.groupBy;
            }
            const key = await this.FETCH_DATA({
                fetch_method: fetch_method,
                requirements,
                filters,
                force,
                params: this.fetchParams,
                keyExtras: this.keyExtras
            });
            Vue.set(this.dataRequirements, id, [widget_requirements, force, use_filters, fetch_method]);
            Vue.set(this.dataKeys, id, key);
        },
        async fetchUnstructuredData(id, requirements) {
            if (!this.currentDashboard)
                return;
            const projectId = this.currentDashboard.project.id;
            const analysisId = this.currentDashboard.analysis.id;
            const analysisModified = this.currentDashboard.analysis.modified;
            const query = mergeDashboardFiltersWithBotanicQuery(requirements.query, this.dashboardMergedFilters, this.dateFieldNames);
            const options = requirements.options;
            const key = await this.FETCH_DATA_QUERY({
                projectId,
                analysisId,
                analysisModified,
                query,
                options,
            });
            Vue.set(this.dataQueryRequirements, id, requirements);
            Vue.set(this.dataKeys, id, key);
        },
        async fetchContextNetworkData(id, requirements, force = false) {
            if (!this.currentDashboard)
                return;
            const analysisModified = this.currentDashboard.analysis.modified;
            const query = mergeDashboardFiltersWithBotanicQuery(requirements.query, this.dashboardMergedFilters, this.dateFieldNames);
            let options = requirements.options;
            // Provide a baseline for determining influence;
            // Useful for calculating expected frequencies when
            // structured data filters are applied.
            const baselineQuery = QueryUtils.extractStructuredFiltersFromQuery(query);
            options = {
                ...options,
                baseline_query: JSON.stringify(baselineQuery)
            };
            const key = await this.FETCH_DATA_CONTEXT_NETWORK({
                analysisModified,
                query,
                options,
                force,
                params: this.fetchParams,
                savedQueries: this.savedQueries
            });
            Vue.set(this.dataContextNetworkRequirements, id, [requirements, force]);
            Vue.set(this.dataKeys, id, key);
        },
        async getVerbatimCount() {
            if (!this.currentDashboard)
                return;
            const projectId = this.currentDashboard.project.id;
            const analysisId = this.currentDashboard.analysis.id;
            const analysisModified = this.currentDashboard.analysis.modified;
            const allDataQuery = {
                type: 'match_all',
                includes: [
                    { type: 'all_data' },
                    { type: 'nonempty_data' },
                ]
            };
            let query;
            if (this.isView['concept']) {
                query = this.filteredConceptQuery;
            }
            else {
                if (this.isView['query'] && this.query) {
                    query = this.themeQuery;
                }
                else {
                    query = {
                        type: 'match_all',
                        includes: [
                            { type: 'all_data' },
                            // Exclude empty verbatims
                            { type: 'nonempty_data' },
                        ],
                    };
                }
            }
            const filteredQuery = mergeDashboardFiltersWithBotanicQuery({ ...query }, this.dashboardMergedFilters, this.dateFieldNames);
            const filteredQueryCompare = mergeDashboardFiltersWithBotanicQuery({ ...query }, this.dashboardMergedFiltersCompare, this.dateFieldNames);
            const options = { start: 0, limit: 0 };
            const key1 = await this.FETCH_DATA_QUERY({
                projectId,
                analysisId,
                analysisModified,
                query: filteredQuery,
                options,
            });
            const key2 = await this.FETCH_DATA_QUERY({
                projectId,
                analysisId,
                analysisModified,
                query: allDataQuery,
                options,
            });
            const key3 = this.compareMode && await this.FETCH_DATA_QUERY({
                projectId,
                analysisId,
                analysisModified,
                query: filteredQueryCompare,
                options,
            });
            Vue.set(this.dataKeys, 'verbatim_count', key1);
            Vue.set(this.dataKeys, 'verbatim_total', key2);
            this.compareMode &&
                Vue.set(this.dataKeys, 'verbatim_count_compare', key3);
        },
        async updateDocumentStats() {
            const requirements = {
                // add block
                blocks: [
                    {
                        aggfuncs: [
                            {
                                new_column: 'frequency_cov',
                                src_column: 'document_id',
                                aggfunc: 'count',
                            },
                        ],
                    }
                ],
                // add queries
                queries: [
                    // a query for the entire set count (denominator)
                    // will be added by default as result group { name: 'overall__' }
                    // query the filtered data count (denominator)
                    {
                        name: 'filtered_all_data',
                        value: this.filteredAllData,
                    },
                    // optionally add the filtered query count (numerator)
                    this.isView['query'] && this.query && {
                        name: 'filtered_query',
                        value: this.filteredQuery,
                    },
                    this.isView['concept'] && this.concepts.length && {
                        name: 'filtered_query',
                        value: this.filteredConceptQuery,
                    },
                    this.compareMode && {
                        name: 'filtered_query_compare',
                        value: this.filteredQueryCompare,
                    },
                ].filter(Boolean),
            };
            if (this.groupBy) {
                requirements.groupby = this.groupBy;
            }
            this.getVerbatimCount();
            const key = await this.FETCH_DATA({
                params: this.fetchParams,
                keyExtras: this.keyExtras,
                requirements,
                filters: []
            });
            Vue.set(this.dataKeys, 'dashboard_stats', key);
        },
        async updateFilterLabel() {
            if (this.currentFiltersTooltip) {
                this.currentFiltersTooltip.dispose();
                this.currentFiltersTooltip = null;
            }
            if (this.dashboardMergedFilters?.length > 0) {
                let html_template = '<div class="active-filters-tooltip" style="font-size: 13px; padding: 5px 3px; min-width: 250px">' +
                    '<h style="color: hsl(196, 12%, 63%); font-weight: bold">Dashboard filtering data by:</h>';
                for (const filter of this.dashboardMergedFilters) {
                    html_template += `
            <p style="margin-bottom: 0;">
              <span style="text-transform: capitalize;">${Util.truncateText(filter.field, 60).replace(/__$/, '')}:</span>
              <strong>${filter.op === 'in' ? '' : filter.op} ${Util.truncateText([].concat(filter.value).map(x => x || '(No Value)').join(', '), 60)}</strong>
            </p>
          `;
                }
                html_template += '</div>';
                let el = this.$el.querySelector('.segment-filter-count');
                if (!el)
                    return;
                this.currentFiltersTooltip = new Tooltip(el, {
                    html: true,
                    container: this.$el,
                    placement: 'bottom',
                    title: html_template,
                    popperOptions: {
                        removeOnDestroy: true
                    }
                });
            }
        },
        async fetchData() {
            await this.updateDocumentStats();
            await this.updateFilterLabel();
        },
        toggleFilter(field, segment) {
            const filter = [{ field, segment }];
            if (field === 'All Segments') {
                const values = segment.split(':');
                filter[0].field = values[0].trim();
                filter[0].segment = values[1].trim();
            }
            this.$emit('toggleFilters', filter);
            this.$analytics.track.dashboard.toggleSegmentFilter(this.currentDashboard.id, field, segment);
        },
        async exportQuery() {
            if (this.numDocumentsQuery > this.exportLimit) {
                this.showDataExportLimitModal = true;
                return;
            }
            this.isExportingQuery = true;
            try {
                let query;
                let exportName = 'overview';
                if (this.isView['conceptOrQuery']) {
                    query = this.isView['query'] ? this.themeQuery : this.conceptQuery;
                    query = mergeDashboardFiltersWithBotanicQuery(query, this.dashboardMergedFilters, this.dateFieldNames);
                    exportName = this.isView['query'] ? 'theme' : 'concept';
                }
                else {
                    query = QueryUtils.convertDashboardFiltersToBotanicQueries(this.dashboardMergedFilters, this.dateFieldNames);
                }
                const res = await Query.runQueryExport(this.currentDashboard.project.id, this.currentDashboard.analysis.id, query, this.savedQueries);
                stringify([res.headers].concat(res.rows), (_, csvString) => {
                    Util.downloadCsv(csvString, `dashboard-${exportName}-result`);
                });
                if (this.isView['query']) {
                    this.$analytics.track.analysis.downloadExport('Dashboard Query Results', 'CSV', {
                        queryName: [this.query.name, ...this.concepts].join(' & '),
                        queryValue: JSON.stringify(this.query),
                        concepts: this.concepts,
                    });
                }
                if (this.isView['concept']) {
                    this.$analytics.track.analysis.downloadExport('Dashboard Concept Results', 'CSV', {
                        concepts: this.concepts.join(' & '),
                    });
                }
            }
            finally {
                this.isExportingQuery = false;
            }
        },
        addConceptToView(newConcept) {
            if (!newConcept)
                return;
            const replacementConcepts = addValueToQueryParam(this.$route, 'concept', newConcept);
            if (replacementConcepts === undefined)
                return;
            this.$router.push({
                path: this.$route.path,
                query: { ...this.$route.query, concept: replacementConcepts },
            });
            this.$emit('scroll-to-top');
        },
        navigateToConcept(concept) {
            if (!concept)
                return;
            let location;
            if (this.inAnalysis) {
                location = {
                    name: 'analysis-dashboard-concept-view',
                    params: { analysisId: `${this.analysisId}`, projectId: `${this.projectId}`, concept: concept },
                    query: { filters: this.$route.query.filters, concept: concept }
                };
            }
            else {
                location = {
                    name: 'dashboard-concept-view',
                    params: { dashboardId: `${this.dashboardId}`, concept: concept },
                    query: { concept: concept, filters: this.$route.query.filters }
                };
            }
            if (!location)
                return;
            this.$router.push(location);
            this.$emit('scroll-to-top');
        },
        navigateToTheme(theme) {
            if (!theme)
                return;
            let location;
            const query = this.expandedDashboardQueries.find(q => q.name === theme);
            if (this.inAnalysis) {
                location = {
                    name: 'analysis-dashboard-query-view',
                    params: {
                        analysisId: this.analysisId,
                        projectId: this.projectId,
                        queryId: query.id
                    },
                    query: {
                        filters: this.$route.query.filters
                    }
                };
            }
            else {
                location = {
                    name: 'dashboard-query-view',
                    params: {
                        dashboardId: this.dashboardId,
                        queryId: query.id
                    },
                    query: {
                        filters: this.$route.query.filters
                    }
                };
            }
            if (!location)
                return;
            this.$router.push(location);
            this.$emit('scroll-to-top');
        },
        calculateColumns(width) {
            return 1 + Math.floor(width / 1000);
        },
        resize(height, width) {
            this.widgetColumns = this.calculateColumns(width);
        },
        configChanged(name, config) {
            const widgets = cloneDeep(this.dashboardWidgetConfig);
            const view = this.dashboardType === 'overview'
                ? 'overview'
                : 'drilldown';
            let foundOne = false;
            const viewConfig = widgets[view].map((widget) => {
                if (widget.name !== name)
                    return widget;
                foundOne = true;
                return {
                    ...widget,
                    ...config,
                };
            });
            if (!foundOne) {
                const defaultConf = defaultConfig().widgets[view].find((w) => w.name === name);
                if (!defaultConf)
                    throw new Error(`Widget ${name} not found in config!`);
                viewConfig.push({
                    ...defaultConf,
                    ...config,
                });
            }
            this.$store.commit(SET_WIDGET_CONFIG, {
                widgets: {
                    ...widgets,
                    [view]: viewConfig
                }
            });
        },
        getWidgetConfig(name) {
            const config = this.dashboardWidgetConfig;
            const view = this.dashboardType === 'overview'
                ? 'overview'
                : 'drilldown';
            const widget = [...config[view]].find((w) => w.name === name);
            if (!widget) {
                // Return empty config if widget is not found
                return {
                    options: {},
                };
            }
            return widget;
        },
        goToPivot(col, row) {
            this.configChanged('pivot-table', {
                options: {
                    'colFields': [col],
                    'rowFields': [row],
                },
            });
            this.$router.push(this.zoomToRoutes['pivot-table']);
        },
        async exportPpt() {
            const pptx = new PptxGenJS();
            for (const widget of this.$refs.widgets) {
                await widget.makePptSlide?.(pptx);
            }
            pptx.writeFile({
                fileName: `${this.currentDashboard.name}.pptx`
            });
            this.$analytics.track.analysis.downloadExport('Export Dashboard', 'PPT', {
                dashboardName: this.currentDashboard.name,
            });
        },
        showGroupedRecords(groupByField, groupByValue, sourceVerbatimId) {
            const baseQuery = this.isView['query'] ? this.themeQuery : this.conceptQuery;
            const newQuery = {
                includes: baseQuery.includes.concat({
                    includes: [
                        { type: "segment", operator: "=", field: groupByField, value: groupByValue }
                    ],
                    type: 'match_any'
                }),
                excludes: baseQuery.excludes,
                type: baseQuery.type,
                level: baseQuery.level
            };
            Query.runQuery(this.$route.params.projectId, this.$route.params.analysisId, newQuery, this.savedQueries, {
                limit: this.maxGroupHits,
                documents: false,
                networkModel: false
            })
                .then((data) => {
                this.showGroupValue = groupByValue;
                this.groupHits = data.hits;
                this.groupSourceVerbatimId = sourceVerbatimId;
                this.numGroupHitsNotShown = Math.max(0, data.total_hits - data.hits.length);
                this.groupHits.sort((x, y) => new Date(x[this.defaultDateField]) - new Date(y[this.defaultDateField]));
                this.viewGroupModalVisible = true;
                //console.log(this.$refs, `verbatim-${sourceVerbatimId}`) //[`verbatim-${sourceVerbatimId}`]) //.$el.scrollIntoView()
                this.$nextTick(() => {
                    this.$el.querySelector('.utterance-highlight').scrollIntoView();
                });
            });
        }
    }
});
export default Overview;
